import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import MCQSetup from "@/pages/MCQSetup";
import MCQTest from "@/pages/MCQTest";
import MCQResults from "@/pages/MCQResults";
import MainsList from "@/pages/MainsList";
import MainsWrite from "@/pages/MainsWrite";
import MainsResult from "@/pages/MainsResult";
import Syllabus from "@/pages/Syllabus";
import PYQ from "@/pages/PYQ";
import PyqUpload from "@/pages/PyqUpload";
import CurrentAffairs from "@/pages/CurrentAffairs";
import DailyEditorials from "@/pages/DailyEditorials";
import DailyArticles from "@/pages/DailyArticles";
import StudyMaterial from "@/pages/StudyMaterial";
import Leaderboard from "@/pages/Leaderboard";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminGuard from "@/pages/admin/AdminGuard";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminQuestions from "@/pages/admin/AdminQuestions";
import AdminPYQPapers from "@/pages/admin/AdminPYQPapers";
import AdminMainsPrompts from "@/pages/admin/AdminMainsPrompts";
import AdminStudy from "@/pages/admin/AdminStudy";
import AdminCurrentAffairs from "@/pages/admin/AdminCurrentAffairs";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminFeedback from "@/pages/admin/AdminFeedback";
import AdminRepositories from "@/pages/admin/AdminRepositories";
import AdminRepositoryDetail from "@/pages/admin/AdminRepositoryDetail";
import AdminTranslate from "@/pages/admin/AdminTranslate";
import AdminStudyPlan from "@/pages/admin/AdminStudyPlan";

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-[#0B1120] text-slate-400" data-testid="auth-loading">
                Loading…
            </div>
        );
    }
    if (!user) return <Navigate to="/" replace />;
    return children;
}

function AppRouter() {
    const location = useLocation();
    // Check URL fragment (not query params) for session_id
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/mcq" element={<ProtectedRoute><MCQSetup /></ProtectedRoute>} />
            <Route path="/mcq/test/:sessionId" element={<ProtectedRoute><MCQTest /></ProtectedRoute>} />
            <Route path="/mcq/result/:sessionId" element={<ProtectedRoute><MCQResults /></ProtectedRoute>} />
            <Route path="/mains" element={<ProtectedRoute><MainsList /></ProtectedRoute>} />
            <Route path="/mains/write/:promptId" element={<ProtectedRoute><MainsWrite /></ProtectedRoute>} />
            <Route path="/mains/:submissionId" element={<ProtectedRoute><MainsResult /></ProtectedRoute>} />
            <Route path="/pyq" element={<ProtectedRoute><PYQ /></ProtectedRoute>} />
            <Route path="/admin/pyq/upload" element={<ProtectedRoute><AdminGuard><PyqUpload /></AdminGuard></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminGuard><AdminLayout /></AdminGuard></ProtectedRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="repositories" element={<AdminRepositories />} />
                <Route path="repositories/:slug" element={<AdminRepositoryDetail />} />
                <Route path="questions" element={<AdminQuestions />} />
                <Route path="pyq" element={<AdminPYQPapers />} />
                <Route path="mains" element={<AdminMainsPrompts />} />
                <Route path="study" element={<AdminStudy />} />
                <Route path="current-affairs" element={<AdminCurrentAffairs />} />
                <Route path="translate" element={<AdminTranslate />} />
                <Route path="study-plan" element={<AdminStudyPlan />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="feedback" element={<AdminFeedback />} />
            </Route>
            <Route path="/syllabus" element={<ProtectedRoute><Syllabus /></ProtectedRoute>} />
            <Route path="/study" element={<ProtectedRoute><StudyMaterial /></ProtectedRoute>} />
            <Route path="/current-affairs" element={<ProtectedRoute><CurrentAffairs /></ProtectedRoute>} />
            <Route path="/current-affairs/articles" element={<ProtectedRoute><DailyArticles /></ProtectedRoute>} />
            <Route path="/current-affairs/editorials" element={<ProtectedRoute><DailyEditorials /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Toaster theme="light" position="top-right" richColors />
                    <AppRouter />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
